import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/header';
import { Footer } from '@/components/footer';
import CTA from '@/components/cta';

export default function AboutUsPage() {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 lg:py-36 overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-black/40" />
            <video
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            >
              <source src="/oleyes_banner_video_3.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-korolev font-bold leading-tight tracking-wide text-white dark:text-white">
                {t('aboutUs.title', 'About Oleyes Security')}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-slate-200 max-w-3xl mx-auto">
                {t('aboutUs.subtitle', 'Empowering Teams with AI-Powered Security')}
              </p>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-korolev font-bold text-slate-900 dark:text-white mb-8">
                {t('aboutUs.ourStory.title', 'Our Story')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
                {t('aboutUs.ourStory.content', 'Oleyes Security was founded by a team of security experts and AI researchers who recognized the growing need for intelligent security solutions in the age of AI. Our journey began in 2022 with a simple mission: to make AI security accessible and effective for teams of all sizes.')}
              </p>
            </div>
          </div>
        </section>

        {/* What We Build Section */}
        {/* About the Company Section */}
        <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-korolev font-bold text-slate-900 dark:text-white mb-6 text-center">
                {t('aboutUs.aboutCompany.title', 'About the Company')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-12 text-center">
                {t('aboutUs.aboutCompany.content', 'Oleyes Security is a cloud video and AI platform built for organizations that want speed, security, and simplicity.')}
              </p>

              <div className="grid md:grid-cols-2 gap-12 mt-16">
                {/* What We Build */}
                <div>
                  <h3 className="text-2xl font-korolev font-bold text-slate-900 dark:text-white mb-6">
                    {t('aboutUs.aboutCompany.whatWeBuild.title', 'What we build')}
                  </h3>
                  <ul className="space-y-4">
                    {(
                      t('aboutUs.aboutCompany.whatWeBuild.items', {
                        returnObjects: true,
                        defaultValue: [
                          'Oleyes Security Gateway – A compact, plug-and-play bridge that auto-discovers cameras & IoT and connects them securely to the cloud using Oleyes PUSH technology.',
                          'Pure Cloud Video + AI – Centralized monitoring, instant alerts, and search across sites with end-to-end encryption.',
                          'AR Visual Audits – Plan camera placement, reveal blind spots, and simulate coverage—before drilling a single hole.',
                          'Open Integrations – API-first design to fit your tools, not the other way around.'
                        ]
                      }) as string[]
                    ).map((item: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1">•</span>
                        <span className="text-slate-600 dark:text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Who We Serve */}
                <div>
                  <h3 className="text-2xl font-korolev font-bold text-slate-900 dark:text-white mb-6">
                    {t('aboutUs.aboutCompany.whoWeServe.title', 'Who we serve')}
                  </h3>
                  <ul className="space-y-4">
                    {(
                      t('aboutUs.aboutCompany.whoWeServe.items', {
                        returnObjects: true,
                        defaultValue: [
                          'Retail, malls & quick-commerce',
                          'Banks & financial institutions',
                          'Pharmacies, healthcare & education',
                          'Security/IT service partners and multi-site enterprises'
                        ]
                      }) as string[]
                    ).map((item: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1">•</span>
                        <span className="text-slate-600 dark:text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-korolev font-bold text-slate-900 dark:text-white mb-4">
                  {t('aboutUs.mission.title', 'Our Mission')}
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {t('aboutUs.mission.content', 'To empower organizations with intelligent security solutions that are as dynamic as the threats they face, enabling them to innovate with confidence.')}
                </p>
              </div>
              <div>
                <h3 className="text-2xl font-korolev font-bold text-slate-900 dark:text-white mb-4">
                  {t('aboutUs.vision.title', 'Our Vision')}
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {t('aboutUs.vision.content', 'A world where security is not a barrier to innovation, but an enabler of it. We envision a future where AI and security work in harmony to create safer digital experiences.')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-korolev font-bold text-center text-slate-900 dark:text-white mb-12">
              {t('aboutUs.ourValues.title', 'Our Values')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: t('aboutUs.ourValues.values.innovation.title', 'Innovation'),
                  content: t('aboutUs.ourValues.values.innovation.content', 'We continuously push the boundaries of what\'s possible in AI security.')
                },
                {
                  title: t('aboutUs.ourValues.values.integrity.title', 'Integrity'),
                  content: t('aboutUs.ourValues.values.integrity.content', 'We maintain the highest standards of honesty and ethical conduct in all we do.')
                },
                {
                  title: t('aboutUs.ourValues.values.excellence.title', 'Excellence'),
                  content: t('aboutUs.ourValues.values.excellence.content', 'We strive for the highest quality in our products, services, and customer support.')
                },
                {
                  title: t('aboutUs.ourValues.values.collaboration.title', 'Collaboration'),
                  content: t('aboutUs.ourValues.values.collaboration.content', 'We believe in the power of teamwork and partnership to achieve great things.')
                },
                {
                  title: t('aboutUs.ourValues.values.customerFocus.title', 'Customer Focus'),
                  content: t('aboutUs.ourValues.values.customerFocus.content', 'We prioritize our customers\' needs and success in everything we do.')
                },
                {
                  title: t('aboutUs.ourValues.values.continuousLearning.title', 'Continuous Learning'),
                  content: t('aboutUs.ourValues.values.continuousLearning.content', 'We embrace curiosity and are committed to ongoing growth and development.')
                }
              ].map((value, index) => (
                <div key={index} className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                  <h3 className="text-xl font-korolev font-bold text-slate-900 dark:text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {value.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How Oleyes Works Section */}
        <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-korolev font-bold text-slate-900 dark:text-white mb-8">
                {t('aboutUs.howItWorks.title', 'How Oleyes Works')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-12">
                {t('aboutUs.howItWorks.content', 'Our platform combines advanced AI algorithms with comprehensive security protocols to provide robust protection. Here\'s a simplified overview of our process:')}
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 text-left mt-12">
                {[
                  {
                    title: t('aboutUs.steps.detection.title', '1. Threat Detection'),
                    content: t('aboutUs.steps.detection.content', 'Our AI continuously monitors for potential security threats in real-time.')
                  },
                  {
                    title: t('aboutUs.steps.analysis.title', '2. Intelligent Analysis'),
                    content: t('aboutUs.steps.analysis.content', 'Machine learning algorithms analyze patterns and behaviors to identify risks.')
                  },
                  {
                    title: t('aboutUs.steps.protection.title', '3. Proactive Protection'),
                    content: t('aboutUs.steps.protection.content', 'Automated responses and alerts help prevent security breaches before they occur.')
                  }
                ].map((step, index) => (
                  <div key={index} className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-sm">
                    <h3 className="text-xl font-korolev font-bold text-slate-900 dark:text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300">
                      {step.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Impact Areas Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-korolev font-bold text-center text-slate-900 dark:text-white mb-12">
              {t('aboutUs.impactAreas.title', 'Our Impact Areas')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: t('aboutUs.impacts.security.title', 'Enhanced Security'),
                  content: t('aboutUs.impacts.security.content', 'Advanced protection against evolving cyber threats')
                },
                {
                  title: t('aboutUs.impacts.compliance.title', 'Regulatory Compliance'),
                  content: t('aboutUs.impacts.compliance.content', 'Simplified compliance with industry standards')
                },
                {
                  title: t('aboutUs.impacts.efficiency.title', 'Operational Efficiency'),
                  content: t('aboutUs.impacts.efficiency.content', 'Automated processes that save time and resources')
                },
                {
                  title: t('aboutUs.impacts.innovation.title', 'Innovation Enablement'),
                  content: t('aboutUs.impacts.innovation.content', 'Secure environment for digital transformation')
                }
              ].map((impact, index) => (
                <div key={index} className="bg-slate-50 dark:bg-slate-800 p-6 rounded-lg">
                  <h3 className="text-xl font-korolev font-bold text-slate-900 dark:text-white mb-3">
                    {impact.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {impact.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Oleyes Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-korolev font-bold text-white mb-6">
                {t('aboutUs.whyChooseUs.title', 'Why Teams Choose Oleyes')}
              </h2>
              <p className="text-lg text-slate-200 max-w-3xl mx-auto">
                {t('aboutUs.whyChooseUs.subtitle', 'Join thousands of organizations that trust Oleyes for their security needs')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: t('aboutUs.whyChooseUs.reasons.expertise.title', 'Expertise You Can Trust'),
                  content: t('aboutUs.whyChooseUs.reasons.expertise.content', 'Our team brings decades of combined experience in AI and cybersecurity.')
                },
                {
                  title: t('aboutUs.whyChooseUs.reasons.technology.title', 'Cutting-Edge Technology'),
                  content: t('aboutUs.whyChooseUs.reasons.technology.content', 'We stay ahead of the curve with the latest advancements in AI security.')
                },
                {
                  title: t('aboutUs.whyChooseUs.reasons.support.title', 'Dedicated Support'),
                  content: t('aboutUs.whyChooseUs.reasons.support.content', 'Our customer success team is here to help you every step of the way.')
                }
              ].map((reason, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10">
                  <h3 className="text-xl font-korolev font-bold text-white mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-slate-200">
                    {reason.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <CTA />
      <Footer />
    </div>
  );
}
